import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MessageSquareHeart, Bug, Lightbulb, MessageSquare, Star, Send, Trash2, Loader2, CheckCircle2, CheckCheck, RotateCcw, Mail, Home, EyeOff, UserX, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FileUpload } from "@/components/FileUpload";

const CATEGORIES = [
  { id: "general", label: "General feedback", icon: MessageSquare },
  { id: "bug", label: "Report an issue", icon: Bug },
  { id: "feature", label: "Suggest a feature", icon: Lightbulb },
];

const CAT_META = {
  general: { label: "General", cls: "bg-slate-100 text-slate-700" },
  bug: { label: "Issue", cls: "bg-red-100 text-red-700" },
  feature: { label: "Feature idea", cls: "bg-emerald-100 text-emerald-700" },
};

export default function Feedback() {
  const { user } = useAuth();
  const [category, setCategory] = useState("general");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [items, setItems] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/feedback"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Please add a short message");
    setBusy(true);
    try {
      await api.post("/feedback", { category, rating: rating || null, subject: subject.trim(), message: message.trim(), anonymous });
      setSent(true);
      setCategory("general"); setRating(0); setSubject(""); setMessage(""); setAnonymous(false);
      toast.success("Thanks — your feedback has been sent!");
      load();
    } catch { toast.error("Could not send feedback. Please try again."); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/feedback/${id}`); setItems((m) => m.filter((x) => x.id !== id)); }
    catch { toast.error("Could not delete"); }
  };

  const [replyFor, setReplyFor] = useState(null);
  const [replyMsg, setReplyMsg] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [homeFor, setHomeFor] = useState(null);
  const [homeName, setHomeName] = useState("");
  const [homeLogo, setHomeLogo] = useState([]);
  const [homeBusy, setHomeBusy] = useState(false);

  const openHomeDisplay = (m) => { setHomeFor(m); setHomeName(m.company_name || ""); setHomeLogo(m.logo_file_id ? [{ file_id: m.logo_file_id, filename: "logo", content_type: "image/*" }] : []); };
  const saveHomeDisplay = async () => {
    setHomeBusy(true);
    try {
      const logo_file_id = homeLogo?.[0]?.file_id || "";
      const { data } = await api.patch(`/feedback/${homeFor.id}/home`, { company_name: homeName.trim(), logo_file_id });
      setItems((list) => list.map((x) => (x.id === homeFor.id ? { ...x, company_name: data.company_name ?? homeName.trim(), logo_file_id: data.logo_file_id ?? logo_file_id } : x)));
      toast.success("Homepage display updated");
      setHomeFor(null);
    } catch { toast.error("Could not save"); }
    setHomeBusy(false);
  };

  const toggleHandled = async (m) => {
    try {
      const { data } = await api.patch(`/feedback/${m.id}`, { handled: !m.handled });
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, handled: data.handled } : x)));
    } catch { toast.error("Could not update status"); }
  };

  const toggleHome = async (m) => {
    try {
      const { data } = await api.patch(`/feedback/${m.id}/home`, { show_on_home: !m.show_on_home });
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, show_on_home: data.show_on_home } : x)));
      toast.success(data.show_on_home ? "Now showing on the homepage" : "Removed from the homepage");
    } catch { toast.error("Could not update"); }
  };

  const openReply = (m) => { setReplyFor(m); setReplyMsg(""); };

  const sendReply = async () => {
    if (!replyMsg.trim()) return toast.error("Write a reply first");
    setReplyBusy(true);
    try {
      await api.post(`/feedback/${replyFor.id}/reply`, { message: replyMsg.trim() });
      setItems((list) => list.map((x) => (x.id === replyFor.id ? { ...x, handled: true } : x)));
      toast.success(`Reply sent to ${replyFor.email}`);
      setReplyFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reply"); }
    setReplyBusy(false);
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="feedback-page">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center"><MessageSquareHeart size={22} /></span>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-slate-900">Help us improve HaulCheck</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mt-1">Feedback</p>
        </div>
      </div>
      <p className="mt-4 text-slate-600 text-base leading-relaxed">
        We're always looking for ways to make HaulCheck better. Share your feedback, report any issues, or suggest new features. Your input helps us improve the platform and ensure it continues to meet your compliance needs.
      </p>

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        {sent ? (
          <div data-testid="feedback-success" className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><CheckCircle2 size={24} /></div>
            <h2 className="font-heading text-xl font-bold text-slate-900 mt-4">Thank you!</h2>
            <p className="text-slate-500 text-sm mt-2">Thank you for helping us improve HaulCheck. We read every message.</p>
            <button onClick={() => setSent(false)} className="mt-5 text-sm font-semibold text-slate-900 hover:underline" data-testid="feedback-send-another">Send more feedback</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <div>
              <Label className="mb-2 block">What's this about?</Label>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="feedback-categories">
                {CATEGORIES.map((c) => (
                  <button type="button" key={c.id} data-testid={`feedback-cat-${c.id}`} onClick={() => setCategory(c.id)}
                    className={cn(
                      "flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-all",
                      category === c.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"
                    )}>
                    <c.icon size={17} /> {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">How would you rate HaulCheck? <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="flex items-center gap-1.5" data-testid="feedback-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} data-testid={`feedback-star-${n}`}
                    onClick={() => setRating(n === rating ? 0 : n)}
                    onMouseEnter={() => setHoverRating(n)} onMouseLeave={() => setHoverRating(0)}
                    className="p-0.5 transition-transform hover:scale-110">
                    <Star size={26} className={cn("transition-colors", (hoverRating || rating) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300")} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="fb-subject">Subject <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="fb-subject" data-testid="feedback-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A short summary" className="mt-1.5" />
            </div>

            <div>
              <Label htmlFor="fb-message">Your feedback</Label>
              <Textarea id="fb-message" data-testid="feedback-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={5} className="mt-1.5"
                placeholder="Tell us what's working well, what's not, or what you'd love to see next…" />
            </div>

            <label data-testid="feedback-anonymous" className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-slate-400 transition-colors">
              <input type="checkbox" data-testid="feedback-anonymous-checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900" />
              <span className="text-sm">
                <span className="font-semibold text-slate-900 flex items-center gap-1.5"><UserX size={15} /> Send anonymously</span>
                <span className="text-slate-500 block mt-0.5">We won't attach your name or email — you'll just be shown as "Anonymous". Note: we won't be able to reply directly.</span>
              </span>
            </label>

            <Button type="submit" data-testid="feedback-submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send feedback
            </Button>
          </form>
        )}
      </div>

      {items.length > 0 && (
        <div className="mt-10" data-testid="feedback-list">
          <h2 className="font-heading text-lg font-bold text-slate-900">
            {user?.is_admin ? "All feedback" : "Your feedback"} <span className="text-slate-400 font-normal">({items.length})</span>
          </h2>
          <div className="mt-4 space-y-3">
            {items.map((m) => (
              <div key={m.id} data-testid="feedback-row" className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={cn("text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5", (CAT_META[m.category] || CAT_META.general).cls)}>
                        {(CAT_META[m.category] || CAT_META.general).label}
                      </span>
                      {m.handled && (
                        <span data-testid="feedback-actioned-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700">
                          <CheckCheck size={11} /> Actioned
                        </span>
                      )}
                      {m.anonymous && (
                        <span data-testid="feedback-anon-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-200 text-slate-600">
                          <UserX size={11} /> Anonymous
                        </span>
                      )}
                      {user?.is_admin && m.show_on_home && (
                        <span data-testid="feedback-onhome-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-blue-100 text-blue-700">
                          <Home size={11} /> On homepage
                        </span>
                      )}
                      {m.rating ? (
                        <span className="flex items-center gap-0.5 text-amber-400">
                          {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={12} className={n <= m.rating ? "fill-amber-400" : "text-slate-200"} />)}
                        </span>
                      ) : null}
                      {user?.is_admin && m.email && <span className="text-xs text-slate-400">{m.name} &lt;{m.email}&gt;</span>}
                    </div>
                    {m.subject && <p className="font-semibold text-slate-900 text-sm mt-2">{m.subject}</p>}
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{m.message}</p>
                    {user?.is_admin && (
                      <div className="flex items-center gap-2 mt-3">
                        <button data-testid="feedback-toggle-handled" onClick={() => toggleHandled(m)}
                          className={cn("inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors",
                            m.handled ? "text-slate-500 hover:bg-slate-100" : "bg-emerald-600 text-white hover:bg-emerald-700")}>
                          {m.handled ? <><RotateCcw size={13} /> Reopen</> : <><CheckCheck size={13} /> Mark actioned</>}
                        </button>
                        {m.email && (
                          <button data-testid="feedback-reply" onClick={() => openReply(m)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                            <Mail size={13} /> Reply
                          </button>
                        )}
                        <button data-testid="feedback-toggle-home" onClick={() => toggleHome(m)}
                          className={cn("inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors",
                            m.show_on_home ? "text-blue-700 hover:bg-blue-50 border border-blue-200" : "border border-slate-300 text-slate-700 hover:bg-slate-50")}>
                          {m.show_on_home ? <><EyeOff size={13} /> Hide from homepage</> : <><Home size={13} /> Show on homepage</>}
                        </button>
                        {m.show_on_home && (
                          <button data-testid="feedback-home-display" onClick={() => openHomeDisplay(m)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                            <Building2 size={13} /> {m.company_name || m.logo_file_id ? "Edit name / logo" : "Add name / logo"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-[11px] text-slate-400">{(m.created_at || "").slice(0, 10)}</span>
                    <button onClick={() => remove(m.id)} data-testid="feedback-delete" className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!replyFor} onOpenChange={(o) => !o && setReplyFor(null)}>
        <DialogContent className="max-w-lg" data-testid="feedback-reply-dialog">
          <DialogHeader>
            <DialogTitle>Reply to feedback</DialogTitle>
            <DialogDescription>
              We'll email {replyFor?.name || "the sender"} at {replyFor?.email}. Their original message is quoted below your reply.
            </DialogDescription>
          </DialogHeader>
          {replyFor?.message && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap max-h-28 overflow-y-auto">
              {replyFor.message}
            </div>
          )}
          <Textarea data-testid="feedback-reply-message" value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} rows={6}
            placeholder="Type your reply…" className="mt-1" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyFor(null)} data-testid="feedback-reply-cancel">Cancel</Button>
            <Button onClick={sendReply} disabled={replyBusy} data-testid="feedback-reply-send" className="bg-slate-900 hover:bg-slate-800 gap-2">
              {replyBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send reply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!homeFor} onOpenChange={(o) => !o && setHomeFor(null)}>
        <DialogContent className="max-w-lg" data-testid="feedback-home-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 size={18} /> Homepage display</DialogTitle>
            <DialogDescription>
              Optionally show a company name and logo on this featured review. Leave both blank to keep it anonymous ("Verified HaulCheck operator"). Only add these when the operator is happy to be named.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="home-company">Company name <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="home-company" data-testid="feedback-home-company" value={homeName} onChange={(e) => setHomeName(e.target.value)} placeholder="e.g. ABC Haulage Ltd" className="mt-1.5" />
            </div>
            <div>
              <Label className="mb-1.5 block">Company logo <span className="text-slate-400 font-normal">(optional)</span></Label>
              <FileUpload testid="feedback-home-logo" accept="image/*" label="Upload a logo image" attachments={homeLogo} onChange={setHomeLogo} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHomeFor(null)} data-testid="feedback-home-cancel">Cancel</Button>
            <Button onClick={saveHomeDisplay} disabled={homeBusy} data-testid="feedback-home-save" className="bg-slate-900 hover:bg-slate-800 gap-2">
              {homeBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
